// Thin intent-based prefetch cache sitting in front of the existing `api.js`
// axios instance — no new HTTP/data library. Triggered on hover/focus/touch
// of a nav link or action button (see Layout/Sidebar.js, Members/MemberList),
// never on mount, so it never delays initial render.
//
// GET-only by contract: every caller passes a fetcher that is itself one of
// the existing read-only functions exported from `api.js` (getMembersByLibrary,
// getPaymentsByLibrary, ...). Never wrap a mutation (POST/PUT/PATCH/DELETE) in
// this — nothing here guards against that, the caller must not do it.
import { createSingleFlight } from './refreshQueue';
import * as authSession from '../Constant/authSession';

const CACHE_TTL_MS = 30000;

const cache = new Map(); // key -> { data, expiresAt }
const runners = new Map(); // key -> single-flight runner for that resource

const cacheKey = (resourceName, scopeId) => `${scopeId ?? 'none'}:${resourceName}`;

/**
 * Prefetch `resourceName` scoped to `scopeId` (almost always libraryId) using
 * `fetcherFn` (a zero-arg thunk, e.g. `() => getMembersByLibrary(libraryId)`).
 * Concurrent calls for the same key share one in-flight request (reusing
 * refreshQueue's single-flight helper) instead of firing duplicates — quick
 * repeated hovers over the same link cost exactly one network call.
 *
 * Note: the underlying api.js functions don't accept an AbortSignal, so a
 * prefetch already in flight can't be network-cancelled if it's superseded —
 * the single-flight de-dupe above is what keeps concurrent calls to one
 * request per key; there's no true request cancellation here.
 */
export function prefetch(resourceName, scopeId, fetcherFn) {
  if (!scopeId) return Promise.resolve(undefined);

  const key = cacheKey(resourceName, scopeId);
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.data);
  }

  if (!runners.has(key)) {
    runners.set(key, createSingleFlight(fetcherFn));
  }

  return runners.get(key)().then((data) => {
    cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
    return data;
  });
}

/** Returns cached data for resourceName+scopeId if still fresh, else undefined. */
export function readPrefetched(resourceName, scopeId) {
  if (!scopeId) return undefined;
  const cached = cache.get(cacheKey(resourceName, scopeId));
  return cached && cached.expiresAt > Date.now() ? cached.data : undefined;
}

export function clearPrefetchCache() {
  cache.clear();
  runners.clear();
}

// Protected data must not survive a logout (own tab or another tab) or a
// forced/expired-session logout — all three fire the same event.
authSession.onLogoutEvent(clearPrefetchCache);
authSession.onCrossTabLogout(clearPrefetchCache);
